import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { whoDecides, type Form, type FormFolder, type FormListStats, type FormType } from '@jfh/intake-shared';
import { api } from '../lib/api.js';
import { INITIAL_FORMS_BASE_URL } from '../lib/deployment.js';
import { openExternal } from '../lib/teams.js';
import { Icon } from '../components/Icon.js';
import { Modal } from '../components/Modal.js';
import { PageHeader, EmptyState } from '../components/Primitives.js';
import { useToast } from '../components/Toast.js';
import { CardMenu, MenuItem } from '../components/CardMenu.js';
import {
  Breadcrumbs,
  FolderNameModal,
  FolderRow,
  MoveModal,
} from '../components/Folders.js';
import { ShareDialog } from '../components/ShareDialog.js';
import { ChoiceList } from '../components/Choices.js';
import { useIsAdmin } from '../lib/useIsAdmin.js';
import { NEW_FORM_TYPES, changedLine, formStatus } from './formsList.js';
import { dragSource } from '../lib/dnd.js';
import {
  byId,
  canMoveTo,
  childrenOf,
  pathLabel,
  pathTo,
  type MoveItem,
} from '../lib/folders.js';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : undefined);

export function FormsList() {
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [allForms, setAllForms] = useState<Form[] | null>(null);
  const [stats, setStats] = useState<Record<string, FormListStats>>({});
  const [shareFor, setShareFor] = useState<Form | null>(null);
  const isAdmin = useIsAdmin();
  const [folders, setFolders] = useState<FormFolder[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [view, setView] = useState<'active' | 'archived'>('active');
  const [formsBase, setFormsBase] = useState(INITIAL_FORMS_BASE_URL);
  const [query, setQuery] = useState('');
  const [dragItem, setDragItem] = useState<MoveItem | null>(null);
  const [moveItem, setMoveItem] = useState<MoveItem | null>(null);
  const [folderModal, setFolderModal] = useState<
    { mode: 'create' } | { mode: 'rename'; folder: FormFolder } | null
  >(null);

  useEffect(() => {
    api
      .getFormsBaseUrl()
      .then((r) => setFormsBase(r.url))
      .catch(() => undefined);
  }, []);

  // ?archived=1 returns every form; the Active / Archived tabs split it.
  const load = useCallback((quiet = false) => {
    if (!quiet) setAllForms(null);
    setError(null);
    Promise.all([api.listForms(true), api.listFolders()])
      .then(([r, f]) => {
        setAllForms(r.forms);
        setStats(r.stats ?? {});
        setFolders(f.folders);
      })
      .catch((e: unknown) => setError(errMsg(e) ?? 'Couldn’t load the forms'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const forms = useMemo(
    () => (allForms ? allForms.filter((f) => (view === 'archived' ? f.archivedAt : !f.archivedAt)) : null),
    [allForms, view],
  );
  const activeCount = allForms?.filter((f) => !f.archivedAt).length ?? 0;
  const archivedCount = (allForms?.length ?? 0) - activeCount;

  const index = useMemo(() => byId(folders), [folders]);
  // A stale ?folder= (e.g. deleted elsewhere) falls back to the top level.
  const requested = params.get('folder');
  const current = requested && index.has(requested) ? requested : null;
  const path = pathTo(index, current);
  /** A form's folder, treating an unknown folder as top level. */
  const folderOf = useCallback(
    (f: Form) => (f.folderId && index.has(f.folderId) ? f.folderId : null),
    [index],
  );

  const openFolder = (id: string | null) => {
    setQuery('');
    setParams(id ? { folder: id } : {});
  };

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const browsing = view === 'active' && !searching;

  const shownFolders = !forms
    ? []
    : searching
      ? view === 'active'
        ? folders.filter((f) => f.name.toLowerCase().includes(q))
        : []
      : browsing
        ? childrenOf(folders, current)
        : [];
  const shownForms = !forms
    ? []
    : searching
      ? forms.filter((f) => f.name.toLowerCase().includes(q) || f.slug.includes(q))
      : browsing
        ? forms.filter((f) => folderOf(f) === current)
        : forms;
  // Paths help wherever the list isn't a single folder's contents.
  const showPaths = !browsing;

  const countsFor = (id: string) => ({
    folders: folders.filter((f) => f.parentId === id).length,
    forms: (forms ?? []).filter((f) => folderOf(f) === id).length,
  });

  const formItem = (f: Form): MoveItem => ({ kind: 'form', id: f.id, name: f.name, parentId: folderOf(f) });
  const folderItem = (f: FormFolder): MoveItem => ({ kind: 'folder', id: f.id, name: f.name, parentId: f.parentId });
  const dropEnabled = (target: string | null) => (dragItem ? canMoveTo(index, dragItem, target) : false);
  const endDrag = () => setDragItem(null);

  /** Move a form or folder; rethrows so the Move dialog can stay open. */
  const moveTo = async (item: MoveItem, target: string | null) => {
    try {
      if (item.kind === 'form') await api.moveForm(item.id, target);
      else await api.updateFolder(item.id, { parentId: target });
      toast({ title: `Moved to ${pathLabel(index, target)}`, body: item.name, icon: 'check' });
      load(true);
    } catch (e: unknown) {
      toast({ title: 'Couldn’t move it', body: errMsg(e), icon: 'x' });
      throw e;
    }
  };
  const dropTo = (target: string | null) => {
    const item = dragItem;
    setDragItem(null);
    if (item && canMoveTo(index, item, target)) void moveTo(item, target).catch(() => undefined);
  };

  const removeFolder = async (folder: FormFolder) => {
    if (!window.confirm(`Delete the folder "${folder.name}"?`)) return;
    try {
      await api.deleteFolder(folder.id);
      toast({ title: 'Folder deleted', body: folder.name, icon: 'trash' });
      load(true);
    } catch (e: unknown) {
      // 409: not empty — the server says what's still inside.
      toast({ title: `Can’t delete “${folder.name}”`, body: errMsg(e), icon: 'x' });
    }
  };

  const archive = async (form: Form) => {
    try {
      await api.archiveForm(form.id);
      toast({ title: 'Form archived', body: form.name, icon: 'check' });
      load(true);
    } catch (e: unknown) {
      toast({ title: 'Couldn’t archive it', body: errMsg(e), icon: 'x' });
    }
  };

  const unarchive = async (form: Form) => {
    try {
      await api.unarchiveForm(form.id);
      toast({ title: 'Form restored', body: form.name, icon: 'check' });
      load(true);
    } catch (e: unknown) {
      toast({ title: 'Couldn’t restore it', body: errMsg(e), icon: 'x' });
    }
  };

  const duplicate = async (form: Form) => {
    try {
      const { form: clone } = await api.cloneForm(form.id);
      toast({ title: 'Form duplicated', body: clone.name, icon: 'check' });
      navigate(`/forms/${clone.id}`);
    } catch (e: unknown) {
      toast({ title: 'Couldn’t duplicate it', body: errMsg(e), icon: 'x' });
    }
  };

  const remove = async (form: Form) => {
    if (!window.confirm(`Delete "${form.name}"? This cannot be undone.`)) return;
    try {
      await api.deleteForm(form.id);
      toast({ title: 'Form deleted', body: form.name, icon: 'trash' });
      load(true);
    } catch (e: unknown) {
      // 409: form has applications — surface the server message, keep it in the list.
      toast({ title: 'Can’t delete the form', body: errMsg(e), icon: 'x' });
    }
  };

  const nothingShown = forms !== null && shownFolders.length === 0 && shownForms.length === 0;

  return (
    <div style={{ padding: 26 }} className="fade-in">
      <PageHeader
        title="Forms"
        subtitle="The forms applicants fill in. Open one to change its questions or publish it."
        actions={
          <>
            {view === 'active' && (
              <button className="btn" onClick={() => setFolderModal({ mode: 'create' })}>
                <Icon name="folderPlus" size={17} /> New folder
              </button>
            )}
            <button className="btn primary" onClick={() => setCreateOpen(true)}>
              <Icon name="plus" size={17} /> New form
            </button>
          </>
        }
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginTop: 18,
        }}
      >
        <div
          role="tablist"
          style={{
            display: 'flex',
            gap: 3,
            background: 'var(--bg-sunken)',
            padding: 3,
            borderRadius: 8,
            width: 'fit-content',
          }}
        >
          {(
            [
              ['active', 'Active', activeCount],
              ['archived', 'Archived', archivedCount],
            ] as const
          ).map(([v, l, n]) => (
            <button
              key={v}
              role="tab"
              aria-selected={view === v}
              onClick={() => setView(v)}
              style={{
                border: 'none',
                background: view === v ? 'var(--bg-surface)' : 'transparent',
                color: view === v ? 'var(--text-1)' : 'var(--text-2)',
                fontWeight: 600,
                fontSize: 12.5,
                padding: '6px 16px',
                borderRadius: 6,
                boxShadow: view === v ? 'var(--sh-xs)' : 'none',
                display: 'flex',
                gap: 7,
              }}
            >
              {l}
              {allForms && <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{n}</span>}
            </button>
          ))}
        </div>
        <div style={{ position: 'relative', width: 280, maxWidth: '100%' }}>
          <Icon
            name="search"
            size={15}
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}
          />
          <input
            className="input"
            type="search"
            aria-label="Search forms and folders"
            placeholder={view === 'active' ? 'Search all forms and folders' : 'Search archived forms'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ paddingLeft: 32 }}
          />
        </div>
      </div>

      {browsing && (
        <div style={{ marginTop: 14 }}>
          <Breadcrumbs path={path} onOpen={openFolder} dropEnabled={dropEnabled} onDropTo={dropTo} />
        </div>
      )}

      {error && (
        <div className="card" style={{ marginTop: 20, padding: 16, color: 'var(--st-declined)' }}>
          {error}
        </div>
      )}

      {!error && nothingShown && (
        <div className="card" style={{ marginTop: 16 }}>
          {searching ? (
            <EmptyState icon="search" title="No matches" body={`Nothing matches “${query.trim()}”.`} />
          ) : view === 'archived' ? (
            <EmptyState
              icon="forms"
              title="No archived forms"
              body="Archived forms are hidden here and on the website."
            />
          ) : current ? (
            <EmptyState
              icon="folder"
              title="This folder is empty"
              body="Create a form here, or move forms into this folder."
            />
          ) : (
            <EmptyState
              icon="forms"
              title="No forms yet"
              body="Create your first form to start collecting trade, credit or supplier applications."
            />
          )}
        </div>
      )}

      {(shownFolders.length > 0 || shownForms.length > 0) && (
        <div className="card" style={{ marginTop: 14, padding: 0, overflow: 'visible' }}>
          <div className="forms-table-head" role="row">
            <span>Name</span>
            <span className="forms-table-mid">Who decides</span>
            <span className="forms-table-mid">Status</span>
            <span className="forms-table-mid">Last 30 days</span>
            <span className="forms-table-changed">Last changed</span>
            <span />
          </div>
          {shownFolders.map((f) => (
            <FolderRow
              key={f.id}
              folder={f}
              path={searching ? `In ${pathLabel(index, f.parentId)}` : undefined}
              counts={countsFor(f.id)}
              onOpen={() => openFolder(f.id)}
              onRename={() => setFolderModal({ mode: 'rename', folder: f })}
              onMove={() => setMoveItem(folderItem(f))}
              onDelete={() => void removeFolder(f)}
              drag={dragSource(folderItem(f), setDragItem, endDrag)}
              dropEnabled={dropEnabled(f.id)}
              onDrop={() => dropTo(f.id)}
              dimmed={dragItem?.id === f.id}
            />
          ))}
          {shownForms.map((f) => (
            <FormRow
              key={f.id}
              form={f}
              stats={stats[f.id]}
              formsBase={formsBase}
              path={showPaths ? pathLabel(index, folderOf(f)) : undefined}
              folderLabel={pathLabel(index, folderOf(f))}
              canShare={isAdmin}
              drag={dragSource(formItem(f), setDragItem, endDrag)}
              dimmed={dragItem?.id === f.id}
              onShare={() => setShareFor(f)}
              onMove={() => setMoveItem(formItem(f))}
              onArchive={() => void archive(f)}
              onUnarchive={() => void unarchive(f)}
              onDuplicate={() => void duplicate(f)}
              onDelete={() => void remove(f)}
            />
          ))}
        </div>
      )}

      <ShareDialog
        target={shareFor ? { name: shareFor.name, slug: shareFor.slug, live: !shareFor.archivedAt && shareFor.status === 'published' } : null}
        baseUrl={formsBase}
        onClose={() => setShareFor(null)}
      />

      <NewFormModal
        open={createOpen}
        folderId={view === 'active' ? current : null}
        folderLabel={view === 'active' && current ? pathLabel(index, current) : null}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          if (view === 'active') load(true);
          else setView('active');
        }}
      />

      <FolderNameModal
        open={folderModal !== null}
        title={folderModal?.mode === 'rename' ? 'Rename folder' : 'New folder'}
        initialName={folderModal?.mode === 'rename' ? folderModal.folder.name : ''}
        where={folderModal?.mode === 'create' ? `In ${pathLabel(index, current)}` : undefined}
        submitLabel={folderModal?.mode === 'rename' ? 'Save' : 'Create folder'}
        onClose={() => setFolderModal(null)}
        onSave={async (name) => {
          if (folderModal?.mode === 'rename') {
            await api.updateFolder(folderModal.folder.id, { name });
            toast({ title: 'Folder renamed', body: name, icon: 'check' });
          } else {
            await api.createFolder({ name, parentId: current });
            toast({ title: 'Folder created', body: name, icon: 'folder' });
          }
          load(true);
        }}
      />

      <MoveModal
        item={moveItem}
        folders={folders}
        index={index}
        onClose={() => setMoveItem(null)}
        onMove={moveTo}
      />
    </div>
  );
}

function FormRow({
  form,
  stats,
  formsBase,
  path,
  folderLabel,
  canShare,
  drag,
  dimmed,
  onShare,
  onMove,
  onArchive,
  onUnarchive,
  onDuplicate,
  onDelete,
}: {
  form: Form;
  stats: FormListStats | undefined;
  formsBase: string;
  /** Folder path, shown when the list spans folders. */
  path?: string;
  /** The folder it's in now ("All forms" at the top level). */
  folderLabel: string;
  canShare: boolean | null;
  drag: ReturnType<typeof dragSource>;
  dimmed?: boolean;
  onShare: () => void;
  onMove: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const navigate = useNavigate();
  const isArchived = form.archivedAt !== null;
  const wix = form.settings.source === 'wix';
  const status = formStatus(form, stats);
  const live = !isArchived && form.status === 'published' && form.currentVersionId !== null;
  const hasApplications = (stats?.total ?? 0) > 0;
  const open = () => navigate(`/forms/${form.id}`);

  return (
    <div
      {...drag}
      role="button"
      tabIndex={0}
      aria-label={`Open ${form.name} in the builder`}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target === e.currentTarget) open();
      }}
      className="forms-table-row"
      style={{ opacity: dimmed ? 0.45 : 1 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <span className="forms-table-icon" style={{ background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>
          <Icon name={wix ? 'link' : 'forms'} size={18} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {form.name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {path ? `In ${path}` : wix ? 'Wix form on the website' : <span className="mono">/f/{form.slug}</span>}
          </div>
        </div>
      </div>
      <div className="forms-table-mid" style={{ fontSize: 13, color: 'var(--text-2)' }}>
        {whoDecides(form.type, form.settings)}
      </div>
      <div className="forms-table-mid" style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
        <span className={`badge ${status.tone}`}>
          {status.tone === 'approved' && <span className="dot" />}
          {status.label}
        </span>
        {status.note && <span style={{ fontSize: 11.5, color: 'var(--text-2)', fontWeight: 700 }}>{status.note}</span>}
      </div>
      <div className="forms-table-mid tnum" style={{ fontSize: 14, fontWeight: 800 }}>
        {stats && (live || stats.total > 0) ? stats.last30 : '—'}
      </div>
      <div className="forms-table-changed" style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
        {changedLine(stats)}
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
        {canShare && live && !wix && (
          <button
            className="btn sm"
            onClick={(e) => {
              e.stopPropagation();
              onShare();
            }}
          >
            <Icon name="link" size={14} /> Share
          </button>
        )}
        <CardMenu label={`Actions for ${form.name}`} width={260}>
          {(close) => (
            <>
              <MenuItem icon="pencil" label="Open in the builder" description="Change questions and publish" onClick={() => { close(); open(); }} />
              {live && !wix && (
                <MenuItem
                  icon="bolt"
                  label="Try it with sample answers"
                  description="Sending it creates a real application"
                  onClick={() => {
                    close();
                    openExternal(`${formsBase}/f/${form.slug}?test=1`);
                  }}
                />
              )}
              <MenuItem icon="copy" label="Duplicate" description="Makes an unpublished copy" onClick={() => { close(); onDuplicate(); }} />
              {!isArchived && (
                <MenuItem icon="folderMove" label="Move to…" description={`Now in ${folderLabel}`} onClick={() => { close(); onMove(); }} />
              )}
              {isArchived ? (
                <MenuItem icon="check" label="Restore" description="Back to the active list" onClick={() => { close(); onUnarchive(); }} />
              ) : (
                <MenuItem icon="inbox" label="Archive" description="Hides it here and on the website" onClick={() => { close(); onArchive(); }} />
              )}
              <MenuItem
                icon="trash"
                label="Delete"
                description={hasApplications ? 'Not possible: it has applications' : 'Removes it for good'}
                danger
                disabled={hasApplications}
                onClick={() => { close(); onDelete(); }}
              />
            </>
          )}
        </CardMenu>
      </div>
    </div>
  );
}

function NewFormModal({
  open,
  folderId,
  folderLabel,
  onClose,
  onCreated,
}: {
  open: boolean;
  /** Folder the form is created in (null = top level). */
  folderId: string | null;
  folderLabel: string | null;
  onClose: () => void;
  onCreated: (form: Form) => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [type, setType] = useState<FormType>('trade_account');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on open.
  useEffect(() => {
    if (open) {
      setName('');
      setType('trade_account');
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { form } = await api.createForm({ name: name.trim(), type, folderId });
      onCreated(form);
      toast({ title: 'Form created', body: form.name, icon: 'plus' });
      onClose();
      navigate(`/forms/${form.id}`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Couldn’t create the form');
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} width={460}>
      <div style={{ padding: 22 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>New form</h3>
        <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 0 }}>
          It starts as a draft{folderLabel ? ` in ${folderLabel}` : ''}. Applicants can’t see it until you publish it.
        </p>

        <label className="field-label">Form name</label>
        <input
          className="input"
          value={name}
          autoFocus
          placeholder="e.g. Trade account application"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
          }}
        />

        <ChoiceList label="What is it for?" value={type} options={NEW_FORM_TYPES} onChange={setType} />

        {error && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--st-declined)' }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={!name.trim() || busy} onClick={() => void submit()}>
            {busy ? 'Creating…' : 'Create and open'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
